/* eslint-disable react/jsx-props-no-spreading, react/prop-types */
import React, { useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import { Provider as ReduxProvider } from 'react-redux'
import { StyleSheetManager, ThemeProvider } from 'styled-components'
import { GoogleOAuthProvider } from '@react-oauth/google'
import builder from '@builder.io/react'
import { StayForLongProvider } from '@s4l/react-context'
import { StayForLong } from '@s4l/stayforlong'

// Polyfills
import 'client/polyfills/containerQuery'
import 'client/polyfills/scrollEnd'
import 'client/polyfills/arrayAt' // eslint-disable-line import/no-unresolved

// Global styles
import '@s4l/utility-css/utility.css'
import '@s4l/ds/reset.css'
import '@s4l/ds/legacy-reset.css'
import '@s4l/ds/tokens.css'
import '@s4l/ds/fonts.css'
import '@s4l/ds/legacy-colors.css'
import '@s4l/ds/legacy-sizes.css'
import '@s4l/ds/rtl.css'

import theme from 'src/styles/themes/lightTheme'

import useMockServer from 'src/hooks/useMockServer'
import useStore from 'src/hooks/useStore'
import GoogleTagManager from 'src/comps/GoogleTagManager'
import Datadog from 'src/comps/Datadog'
import UIKitEventBridge from 'src/comps/UIKitEventBridge'

import { ErrorHandler } from 'src/pages/_app/ErrorHandler'
import { DeviceProvider } from 'src/context/device'
import { MarketProvider } from 'src/context/market'
import { SystemProvider } from 'src/context/system'
import { BusinessProvider } from 'src/context/business/'
import { SessionProvider } from 'src/context/session'
import { BusProvider } from 'src/context/bus'
import { ExperimentProvider } from 'src/context/experiment'
import { SuggesterSearchProvider } from 'src/context/search/SuggesterSearch'

import { getDeviceContextValue } from 'src/context/device/helpers'
import { getMarketContextValue } from 'src/context/market/helpers'
import { getBusinessContextValue } from 'src/context/business/helpers'
import { getSessionContextValue } from 'src/context/session/helpers'
import { getSystemContextValue } from 'src/context/system/helpers'
import { getExperimentContextValue } from 'src/context/experiment/helpers'

import { withLayouts } from '../src/pages/_app/withLayouts'
import { reportPageView } from 'src/utils/clientErrorReporter'
import { ErrorType } from 'src/pages/withSession/errorType'
import { PageProvider } from 'src/comps/Page/context'
import { HotjarUserAttributes } from 'src/comps/HotjarUserAttributes'
import isPropValid from '@emotion/is-prop-valid'

// Avoid scroll restoration
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

export const usePageViewName = pathName => {
  useEffect(() => {
    reportPageView(pathName)
  }, [pathName])
}

const builderApiKey = process.env.NEXT_PUBLIC_BUILDER_API_KEY
if (builderApiKey) {
  builder.init(builderApiKey)
}

const App = ({ Component, pageProps }) => {
  const router = useRouter()

  // Disable prefetch on hover for all links
  // https://github.com/vercel/next.js/discussions/24437#discussioncomment-3679709
  useMemo(() => {
    router.prefetch = async () => {}
  }, [router])

  const { ready } = useMockServer()
  const { config, translations, error } = pageProps
  const locale = config?.locale || 'en'
  const { customer: customerData, isLogged, ...restConfig } = config

  const domain = useRef(
    StayForLong.create({
      ENVIRONMENT: process.env.NEXT_PUBLIC_ENV,
      API_GATEWAY: process.env.NEXT_PUBLIC_API_GATEWAY,
      API_GATEWAY_MAP: new Map([
        ['www.stayforlong.com', 'https://gw.stayforlong.com'],
        ['www.stayforlong.fr', 'https://gw.stayforlong.fr'],
        ['www.stayforlong.co.uk', 'https://gw.stayforlong.co.uk'],
        ['www.stayforlong.de', 'https://gw.stayforlong.de'],
      ]),
      IMAGE_SERVICE: process.env.NEXT_PUBLIC_IMAGE_SERVICE,
      STATICS_HOST: process.env.NEXT_PUBLIC_CLOUDFRONT_ENDPOINT,
      BANNERS_FILE_NAME: process.env.NEXT_PUBLIC_BANNERS_FILE_NAME,
    })
  )

  const isError =
    pageProps.error === ErrorType.NotFound || pageProps.error === ErrorType.ServerError
  usePageViewName(isError ? '_error' : Component.pagePathView)

  const isNode = typeof window === 'undefined'

  if (!isNode && window.localStorage.getItem('STAYFORLONG_EXPOSE_DOMAIN')) {
    window.domain = domain.current
  }

  const initialState = {
    login: {
      ...customerData,
    },
    config: {
      ...restConfig,
      pageId: Component.pageId,
    },
    ...(pageProps.property && {
      property: {
        property: pageProps.property,
      },
    }),
  }

  const { store } = useStore({ initialState })

  if (!ready || !store) return <></>

  const marketValue = getMarketContextValue(config)
  const systemContextValue = getSystemContextValue(config)
  const businessValue = getBusinessContextValue(config)
  const sessionData = getSessionContextValue(config)
  const deviceContextValue = getDeviceContextValue(config)
  const experimentValue = getExperimentContextValue(config)

  return (
    <BusProvider>
      <ReduxProvider store={store}>
        <StyleSheetManager
          enableVendorPrefixes
          shouldForwardProp={(propName, elementToBeRendered) => {
            return typeof elementToBeRendered === 'string' ? isPropValid(propName) : true
          }}
        >
          <MarketProvider value={marketValue}>
            <SystemProvider value={systemContextValue}>
              <BusinessProvider value={businessValue}>
                <SessionProvider value={sessionData}>
                  <DeviceProvider value={deviceContextValue}>
                    <ExperimentProvider value={experimentValue}>
                      <SuggesterSearchProvider>
                        <ThemeProvider theme={theme}>
                          <PageProvider value={{ pageId: Component.pageId }}>
                            <StayForLongProvider
                              key={locale}
                              domain={domain.current}
                              locale={locale}
                              device={deviceContextValue.device}
                              messages={translations}
                              mobileAppVersion={config.mobileAppVersion}
                            >
                              <GoogleOAuthProvider
                                clientId={process.env.NEXT_PUBLIC_GOOGLE_LOGIN_CLIENT_ID}
                              >
                                {config.rejectTracking !== 'true' && <GoogleTagManager />}
                                <HotjarUserAttributes />
                                <Datadog />
                                <UIKitEventBridge />
                                <ErrorHandler {...pageProps}>
                                  {!error && <Component {...pageProps} />}
                                </ErrorHandler>
                              </GoogleOAuthProvider>
                            </StayForLongProvider>
                          </PageProvider>
                        </ThemeProvider>
                      </SuggesterSearchProvider>
                    </ExperimentProvider>
                  </DeviceProvider>
                </SessionProvider>
              </BusinessProvider>
            </SystemProvider>
          </MarketProvider>
        </StyleSheetManager>
      </ReduxProvider>
    </BusProvider>
  )
}

export default withLayouts(App)
